import { apiSlice } from '../../api';
import type {
  DeleteFeeDTO,
  DeleteFeeGroupsDTO,
  DeleteFeeGroupsResult,
  DeleteFeeLevelDTO,
  DeleteFeeLevelResult,
  DeleteFeeResult,
  GetFeeDTO,
  GetFeeGroupsDTO,
  GetFeeGroupsResult,
  GetFeeLevelsDTO,
  GetFeeLevelsResult,
  GetFeeResult,
  GetFeesDTO,
  GetFeesResult,
  PostFeeGroupsDTO,
  PostFeeGroupsResult,
  PostFeeLevelsDTO,
  PostFeeLevelsResult,
  PutFeeDTO,
  PutFeeGroupsDTO,
  PutFeeGroupsResult,
  PutFeeLevelDTO,
  PutFeeLevelResult,
  PutFeeResult,
} from './types';

const feesEndpoints = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getFees: build.query<GetFeesResult, GetFeesDTO>({
      query: ({ fee_group_id, params }) => ({
        url: `/admin/v1/fee-groups/${fee_group_id}/fees`,
        method: 'GET',
        params,
      }),
      providesTags: ['Fees'],
    }),

    getFee: build.mutation<GetFeeResult, GetFeeDTO>({
      query: ({ feeId, fee_group_id, fee_level_id }) => ({
        url: `/admin/v1/fee-groups/${fee_group_id}/fee-levels/${fee_level_id}/fees/${feeId}`,
        method: 'GET',
      }),
    }),

    putFee: build.mutation<PutFeeResult, PutFeeDTO>({
      query: ({ body, fee_group_id, fee_level_id }) => ({
        url: `/admin/v1/fee-groups/${fee_group_id}/fee-levels/${fee_level_id}/fees`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Fees'],
    }),

    deleteFee: build.mutation<DeleteFeeResult, DeleteFeeDTO>({
      query: ({ feeId, fee_group_id, fee_level_id }) => ({
        url: `/admin/v1/fee-groups/${fee_group_id}/fee-levels/${fee_level_id}/fees/${feeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Fees'],
    }),

    getFeeGoups: build.query<GetFeeGroupsResult, GetFeeGroupsDTO>({
      query: () => ({
        url: '/admin/v1/fee-groups',
        method: 'GET',
      }),
      providesTags: ['Fees'],
    }),
    postFeeGoups: build.mutation<PostFeeGroupsResult, PostFeeGroupsDTO>({
      query: (body) => ({
        url: '/admin/v1/fee-groups',
        method: 'POST',
        body,
      }),
    }),
    putFeeGoups: build.mutation<PutFeeGroupsResult, PutFeeGroupsDTO>({
      query: ({ body, fee_group_id }) => ({
        url: `/admin/v1/fee-groups/${fee_group_id}`,
        method: 'PUT',
        body,
      }),
    }),
    deleteFeeGoups: build.mutation<DeleteFeeGroupsResult, DeleteFeeGroupsDTO>({
      query: ({ fee_group_id }) => ({
        url: `/admin/v1/fee-groups/${fee_group_id}`,
        method: 'DELETE',
      }),
    }),
    getFeeLevels: build.query<GetFeeLevelsResult, GetFeeLevelsDTO>({
      query: ({ fee_group_id }) => ({
        url: '/admin/v1/fee-levels',
        method: 'GET',
        params: { fee_group_id },
      }),
    }),
    postFeeLevels: build.mutation<PostFeeLevelsResult, PostFeeLevelsDTO>({
      query: (body) => ({
        url: '/admin/v1/fee-levels',
        method: 'POST',
        body,
      }),
    }),
    putFeeLevels: build.mutation<PutFeeLevelResult, PutFeeLevelDTO>({
      query: ({ fee_level_id, body }) => ({
        url: `/admin/v1/fee-levels/${fee_level_id}`,
        method: 'PUT',
        body,
      }),
    }),
    deleteFeeLevels: build.mutation<DeleteFeeLevelResult, DeleteFeeLevelDTO>({
      query: ({ fee_level_id, fee_group_id }) => ({
        url: `/admin/v1/fee-levels/${fee_level_id}`,
        method: 'DELETE',
        params: { fee_group_id },
      }),
    }),
  }),
});

export const {
  useGetFeesQuery,
  useGetFeeMutation,
  usePutFeeMutation,
  useDeleteFeeMutation,
  useGetFeeGoupsQuery,
  useDeleteFeeGoupsMutation,
  useLazyGetFeeGoupsQuery,
  useLazyGetFeesQuery,
  usePostFeeGoupsMutation,
  usePutFeeGoupsMutation,
  useDeleteFeeLevelsMutation,
  useGetFeeLevelsQuery,
  usePostFeeLevelsMutation,
  usePutFeeLevelsMutation,
  useLazyGetFeeLevelsQuery,
} = feesEndpoints;

export type FeesController = typeof feesEndpoints;
